import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useSettings, type UserSettings } from './SettingsContext';
import { useT } from '../i18n/i18n';

const PIN_REGEX = /^\d{4}$/;

type Mode = 'idle' | 'set' | 'change' | 'clear' | 'forgot';

export default function SupervisorPinSection() {
  const t = useT();
  const { token } = useAuth();
  const { settings, applyServerSettings } = useSettings();
  const [mode, setMode] = useState<Mode>('idle');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function reset() {
    setMode('idle');
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setAccountPassword('');
    setError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (mode === 'set' || mode === 'change') {
      if (!PIN_REGEX.test(newPin)) {
        setError(t('PIN must be exactly 4 digits.'));
        return;
      }
      if (newPin !== confirmPin) {
        setError(t('The two PINs do not match.'));
        return;
      }
      if (mode === 'change' && !PIN_REGEX.test(currentPin)) {
        setError(t('Enter the current 4-digit PIN.'));
        return;
      }
    } else if (mode === 'clear') {
      if (!PIN_REGEX.test(currentPin)) {
        setError(t('Enter the current 4-digit PIN.'));
        return;
      }
    } else if (mode === 'forgot') {
      if (!accountPassword) {
        setError(t('Enter your account password.'));
        return;
      }
      // newPin is optional — empty/blank means "clear the PIN".
      if (newPin && !PIN_REGEX.test(newPin)) {
        setError(t('New PIN must be exactly 4 digits, or leave both new-PIN fields blank to remove it.'));
        return;
      }
      if (newPin !== confirmPin) {
        setError(t('The two new PINs do not match.'));
        return;
      }
    }

    setBusy(true);
    try {
      let updated: Partial<UserSettings>;
      if (mode === 'clear') {
        updated = await api<Partial<UserSettings>>('/me/supervisor-pin/clear', {
          method: 'POST',
          token,
          body: { currentPin },
        });
        setSuccess(t('Supervisor PIN cleared. Home button is no longer guarded.'));
      } else if (mode === 'forgot') {
        updated = await api<Partial<UserSettings>>('/me/supervisor-pin/reset', {
          method: 'POST',
          token,
          body: { password: accountPassword, newPin: newPin || null },
        });
        setSuccess(newPin
          ? t('Supervisor PIN reset to a new value.')
          : t('Supervisor PIN cleared via account password.'));
      } else {
        updated = await api<Partial<UserSettings>>('/me/supervisor-pin', {
          method: 'PUT',
          token,
          body: { currentPin: mode === 'change' ? currentPin : null, newPin },
        });
        setSuccess(mode === 'set' ? t('Supervisor PIN set.') : t('Supervisor PIN changed.'));
      }
      applyServerSettings(updated);
      reset();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('Could not save PIN');
      if (msg.includes('current PIN incorrect')) setError(t('Current PIN is incorrect.'));
      else if (msg.includes('too many wrong PINs')) setError(t('Too many wrong PINs. Wait 5 minutes, then try again.'));
      else if (msg.includes('password incorrect')) setError(t('Account password is incorrect.'));
      else setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>{t('Supervisor PIN')}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('While a learner is playing, the Home button asks for this 4-digit PIN before leaving the game. Set this on the device before handing it to the student.')}
      </p>

      {success && <div className="banner" role="status">{success}</div>}
      {error && <div className="error" role="alert">{error}</div>}

      {mode === 'idle' && (
        <div className="toolbar">
          <span className="muted">
            {t('Status:')} <strong>{settings.supervisorPinSet ? t('PIN is set') : t('Not set')}</strong>
          </span>
          {settings.supervisorPinSet ? (
            <>
              <button className="btn secondary" onClick={() => { setMode('change'); setSuccess(null); }}>
                {t('Change PIN')}
              </button>
              <button className="btn ghost" onClick={() => { setMode('clear'); setSuccess(null); }}>
                {t('Remove PIN')}
              </button>
              <button className="btn ghost" onClick={() => { setMode('forgot'); setSuccess(null); }}>
                {t('Forgot PIN?')}
              </button>
            </>
          ) : (
            <button className="btn" onClick={() => { setMode('set'); setSuccess(null); }}>
              {t('Set PIN')}
            </button>
          )}
        </div>
      )}

      {mode !== 'idle' && (
        <form className="spaced" onSubmit={submit} noValidate>
          {(mode === 'change' || mode === 'clear') && (
            <div className="field">
              <label htmlFor="currentPin">{t('Current PIN')}</label>
              <input
                id="currentPin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                pattern="\d{4}"
                maxLength={4}
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
              />
            </div>
          )}
          {mode === 'forgot' && (
            <div className="field">
              <label htmlFor="accountPassword">{t('Account password')}</label>
              <input
                id="accountPassword"
                type="password"
                autoComplete="current-password"
                value={accountPassword}
                onChange={(e) => setAccountPassword(e.target.value)}
                autoFocus
              />
              <span className="field-hint">
                {t("Confirms it's you. Leave the new-PIN fields blank to remove the PIN entirely.")}
              </span>
            </div>
          )}
          {(mode === 'set' || mode === 'change' || mode === 'forgot') && (
            <>
              <div className="field">
                <label htmlFor="newPin">
                  {mode === 'forgot' ? t('New PIN (optional)') : t('New PIN')}
                </label>
                <input
                  id="newPin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{4}"
                  maxLength={4}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  autoFocus={mode === 'set'}
                />
              </div>
              <div className="field">
                <label htmlFor="confirmPin">
                  {mode === 'forgot' ? t('Confirm new PIN (optional)') : t('Confirm new PIN')}
                </label>
                <input
                  id="confirmPin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{4}"
                  maxLength={4}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
            </>
          )}
          <div className="toolbar">
            <button className="btn" type="submit" disabled={busy}>
              {busy ? t('Saving…') :
                mode === 'set' ? t('Set PIN') :
                  mode === 'change' ? t('Change PIN') :
                    mode === 'clear' ? t('Remove PIN') :
                      newPin ? t('Reset PIN') : t('Remove PIN')}
            </button>
            <button type="button" className="btn secondary" onClick={reset} disabled={busy}>
              {t('Cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
